import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, Linkedin } from "lucide-react";
import { CONFIG } from "@/lib/api";

const COLS = [
  {
    title: "Project",
    links: [
      { to: "/about", label: "About Purva City" },
      { to: "/amenities", label: "Amenities" },
      { to: "/floor-plans", label: "Floor Plans" },
      { to: "/master-plan", label: "Master Plan" },
      { to: "/specifications", label: "Specifications" },
      { to: "/gallery", label: "Gallery" },
      { to: "/price", label: "Price" },
      { to: "/construction-updates", label: "Construction Updates" },
    ],
  },
  {
    title: "Buyer Tools",
    links: [
      { to: "/emi-calculator", label: "EMI Calculator" },
      { to: "/home-loan", label: "Home Loan" },
      { to: "/stamp-duty", label: "Stamp Duty" },
      { to: "/cost-sheet", label: "Cost Sheet" },
      { to: "/faqs", label: "FAQs" },
    ],
  },
  {
    title: "NRI Investment",
    links: [
      { to: "/nri", label: "NRI Centre" },
      { to: "/nri/why-invest", label: "Why Invest" },
      { to: "/nri/fema-rbi", label: "FEMA / RBI" },
      { to: "/nri/home-loans", label: "NRI Home Loans" },
      { to: "/nri/taxation", label: "Taxation" },
      { to: "/nri/buying-process", label: "Buying Process" },
      { to: "/nri/virtual-site-visit", label: "Virtual Site Visit" },
    ],
  },
  {
    title: "Explore",
    links: [
      { to: "/apartments-in-kochi", label: "Apartments in Kochi" },
      { to: "/luxury-apartments-in-kochi", label: "Luxury Apartments Kochi" },
      { to: "/3-bhk-apartments-kochi", label: "3 BHK Apartments" },
      { to: "/4-bhk-apartments-kochi", label: "4 BHK Apartments" },
      { to: "/apartments-near-lulu-mall", label: "Near LuLu Mall" },
      { to: "/puravankara-projects-kochi", label: "Puravankara Kochi" },
      { to: "/blogs", label: "Blog" },
      { to: "/sitemap", label: "Sitemap" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="mt-32 border-t border-[color:var(--pcv-border)] bg-[color:var(--pcv-surface)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 bg-white flex items-center justify-center p-1.5">
                <img src="https://customer-assets.emergentagent.com/job_purva-flagship-kochi/artifacts/msbsnbyo_logo.png" alt="Provident Winworth" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-serif-lux text-2xl">Provident <span className="text-[color:var(--pcv-gold)]">Winworth</span></div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--pcv-text-2)] mt-0.5">Edappally, Kochi</div>
              </div>
            </div>
            <p className="mt-4 text-sm text-[color:var(--pcv-text-2)] leading-relaxed">
              A 16-acre premium residential enclave by Puravankara's Provident Housing — imagined for the modern connoisseur in the heart of Edappally.
            </p>
            <div className="mt-5 flex items-center gap-3 border-t border-[color:var(--pcv-border)] pt-4">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[color:var(--pcv-text-2)]">Developed by</div>
              <img src="https://customer-assets.emergentagent.com/job_purva-flagship-kochi/artifacts/kejco62y_Provident_logo_final_blacks.png" alt="Puravankara" className="h-6 opacity-90 invert" />
            </div>
            <div className="mt-6 space-y-2 text-sm text-[color:var(--pcv-text-2)]">
              <a href={`tel:${CONFIG.phoneRaw}`} className="flex items-center gap-3 hover:text-[color:var(--pcv-gold)]" data-testid="footer-call"><Phone className="w-4 h-4" />{CONFIG.phone}</a>
              <a href={`mailto:${CONFIG.email}`} className="flex items-center gap-3 hover:text-[color:var(--pcv-gold)]" data-testid="footer-email"><Mail className="w-4 h-4" />{CONFIG.email}</a>
              <div className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5" />{CONFIG.location}</div>
            </div>
            <div className="mt-6 flex gap-3 text-[color:var(--pcv-text-2)]">
              <a href="#" aria-label="Instagram" className="p-2 border border-[color:var(--pcv-border)] hover:text-[color:var(--pcv-gold)] hover:border-[color:var(--pcv-gold)]"><Instagram className="w-4 h-4" /></a>
              <a href="#" aria-label="Facebook" className="p-2 border border-[color:var(--pcv-border)] hover:text-[color:var(--pcv-gold)] hover:border-[color:var(--pcv-gold)]"><Facebook className="w-4 h-4" /></a>
              <a href="#" aria-label="YouTube" className="p-2 border border-[color:var(--pcv-border)] hover:text-[color:var(--pcv-gold)] hover:border-[color:var(--pcv-gold)]"><Youtube className="w-4 h-4" /></a>
              <a href="#" aria-label="LinkedIn" className="p-2 border border-[color:var(--pcv-border)] hover:text-[color:var(--pcv-gold)] hover:border-[color:var(--pcv-gold)]"><Linkedin className="w-4 h-4" /></a>
            </div>
          </div>

          {COLS.map((c) => (
            <div key={c.title}>
              <div className="eyebrow mb-4">{c.title}</div>
              <ul className="space-y-2.5">
                {c.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-[color:var(--pcv-text-2)] hover:text-[color:var(--pcv-gold)] transition">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="divider-lux mt-14" />
        <div className="mt-8 flex flex-col md:flex-row justify-between gap-6 text-xs text-[color:var(--pcv-text-2)]">
          <div>
            <div>© {new Date().getFullYear()} Puravankara Limited (Provident Housing). All rights reserved.</div>
            <div className="mt-1">Project RERA · {CONFIG.rera} · <a href="https://rera.kerala.gov.in/" target="_blank" rel="noreferrer" className="hover:text-[color:var(--pcv-gold)]">rera.kerala.gov.in</a></div>
            <div className="mt-1">Marketed by · <strong className="text-[color:var(--pcv-text-2)]">{CONFIG.agentName}</strong> · Agent RERA <strong className="text-[color:var(--pcv-text-2)]">{CONFIG.agentRera}</strong></div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Link to="/privacy" className="hover:text-[color:var(--pcv-gold)]">Privacy</Link>
            <Link to="/terms" className="hover:text-[color:var(--pcv-gold)]">Terms</Link>
            <Link to="/disclaimer" className="hover:text-[color:var(--pcv-gold)]">Disclaimer</Link>
            <Link to="/sitemap" className="hover:text-[color:var(--pcv-gold)]">Sitemap</Link>
            <Link to="/admin/login" className="hover:text-[color:var(--pcv-gold)]" data-testid="footer-admin">Admin</Link>
          </div>
        </div>

        <p className="mt-8 text-[10px] leading-relaxed text-[color:var(--pcv-text-2)]/70 max-w-5xl">
          <strong className="text-[color:var(--pcv-text-2)]">Channel Partner Disclosure:</strong> This site is managed by a RERA-authorised affiliate partner / real estate agent (for multiple real estate developers) namely <strong className="text-[color:var(--pcv-text-2)]">Vidya Menon (K-RERA Agent Reg. K-RERA/AG/0090/2022)</strong>. All offers are made by the builder and subject to change without prior notice. Pricing is subject to alteration without advance notification, and property availability cannot be guaranteed. All offers are dependent solely at the builder's discretion; we are marketing on behalf of the developer and take no responsibility for claims made by the builder. No representation as to the accuracy or completeness of this website is made by us. Images shown are for representational purposes only and are not actual pictures. *Subject to availability. We may share your data with Kerala Real Estate Regulatory Authority (RERA) registered Developers for further processing as necessary, and may send updates to the mobile number or email registered with us.
        </p>
      </div>
    </footer>
  );
}
